import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';

/**
 * One reason, then a confirmed action: suspend or revoke an agent, cancel a
 * print job, mark it handled, reprint a ticket. Optional presets (a dropdown
 * with "Other" for free text). A lifecycle dialog, not a form: no dirty
 * tracking, and the backdrop does not close it (destructive actions).
 */
export interface PrintReasonModalProps {
  isOpen: boolean;
  title: string;
  /** One or two sentences on what happens. */
  message: string;
  confirmLabel: string;
  variant?: 'default' | 'danger';
  /** Presets; "Other" with free text is added. Empty = a text box only. */
  presets?: string[];
  /** A reason is required unless false (e.g. "Mark handled" takes an optional note). */
  required?: boolean;
  label?: string;
  placeholder?: string;
  /** Typed confirmation for a final action: the user must type this word. */
  confirmWord?: string;
  busy?: boolean;
  /** An error from the API, shown above the field; the dialog stays open. */
  error?: string | null;
  /** Extra controls under the reason (e.g. a secondary action). */
  footerExtra?: React.ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const OTHER = '__other__';

const PrintReasonModal: React.FC<PrintReasonModalProps> = ({
  isOpen, title, message, confirmLabel, variant = 'default', presets = [], required = true, label = 'Reason',
  placeholder = 'Say why', confirmWord, busy = false, error, footerExtra, onConfirm, onCancel,
}) => {
  const [choice, setChoice] = useState('');
  const [text, setText] = useState('');
  const [typed, setTyped] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setChoice(presets.length ? presets[0] : OTHER);
    setText('');
    setTyped('');
    setFieldError(null);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const reason = choice === OTHER ? text.trim() : choice;
  const wordOk = !confirmWord || typed.trim().toUpperCase() === confirmWord.toUpperCase();

  const submit = () => {
    if (required && !reason) { setFieldError('A reason is required.'); return; }
    if (!wordOk) { setFieldError(`Type ${confirmWord} to confirm.`); return; }
    onConfirm(reason);
  };

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10040 }} />
      <div className="modal-container modal-sm" role="dialog" aria-modal="true" aria-label={title}
        style={{ minHeight: 'auto', maxWidth: 480, zIndex: 10050 }} data-reason-modal>
        {busy && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Working...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onCancel} disabled={busy} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          <p style={{ marginTop: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{message}</p>
          {error && <div className="form-error-message" style={{ marginBottom: 10 }} data-reason-error>{error}</div>}
          {presets.length > 0 && (
            <div className="form-group">
              <SyutSelect label={<>{label}{required && <span className="text-danger"> *</span>}</>}
                options={[...presets.map((p) => ({ value: p, label: p })), { value: OTHER, label: 'Other…' }]}
                value={choice} size="sm" isClearable={false} menuPortalTarget={document.body}
                onChange={(v) => { setFieldError(null); setChoice((v as string) || OTHER); }} />
            </div>
          )}
          {choice === OTHER && (
            <div className="form-group" data-field="reason">
              {presets.length === 0 && <label className="form-label" htmlFor="prn-reason">{label}{required && <span className="text-danger"> *</span>}</label>}
              <input id="prn-reason" className={`form-input${fieldError && !reason ? ' form-field-error' : ''}`} value={text} autoFocus
                maxLength={500} placeholder={placeholder} onChange={(e) => { setFieldError(null); setText(e.target.value); }}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
            </div>
          )}
          {confirmWord && (
            <div className="form-group">
              <label className="form-label" htmlFor="prn-confirm-word">Type <strong>{confirmWord}</strong> to confirm</label>
              <input id="prn-confirm-word" className="form-input" value={typed} onChange={(e) => { setFieldError(null); setTyped(e.target.value); }} />
            </div>
          )}
          {fieldError && <div className="form-error-message">{fieldError}</div>}
          {footerExtra}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`} onClick={submit}
            disabled={busy || (required && !reason) || !wordOk}>{confirmLabel}</button>
        </div>
      </div>
    </>
  );
};

export default PrintReasonModal;
